import axios from 'axios';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';

type ReservedRange = { start_time: string; end_time: string };

const initialState: string[] = [];

export function useReservedHours(
    reservableType: 'room' | 'table',
    reservableId: string | number | null,
    date: Date | null,
) {
    const [reservedHours, setReservedHours] = useState<string[]>(initialState);
    const [loading, setLoading] = useState(false);

    const fetchReservedHours = async (
        reservableType: 'room' | 'table',
        reservableId: string | number,
        date: Date,
    ) => {
        try {
            const day = format(date, 'yyyy-MM-dd');

            const response = await axios.get<
                | { reserved_hours: ReservedRange[] }
                | { reserved_hours: string[] }
            >(
                `/api/reservations/get-reserved-hours?reservable_type=${reservableType}&reservable_id=${reservableId}&date=${day}`,
            );

            const payload = response.data as
                | { reserved_hours: ReservedRange[] }
                | { reserved_hours: string[] };
            const raw = payload?.reserved_hours ?? [];

            if (Array.isArray(raw) && typeof raw[0] === 'string') {
                setReservedHours(raw as string[]);
                return;
            }

            const slots = new Set<string>();
            const ranges = raw as ReservedRange[];
            for (const r of ranges) {
                const start = new Date(r.start_time);
                const end = new Date(r.end_time);
                const cursor = new Date(start);
                while (cursor < end) {
                    slots.add(format(cursor, 'HH:00'));
                    cursor.setHours(cursor.getHours() + 1);
                }
            }
            setReservedHours(Array.from(slots).sort());
            return response.data.reserved_hours;
        } catch (error) {
            console.error('Error fetching reserved hours:', error);
            setReservedHours(initialState);
            return initialState;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!reservableId || !date) {
            setReservedHours(initialState);
            return;
        }

        setLoading(true);

        fetchReservedHours(reservableType, reservableId, date);
    }, [reservableType, reservableId, date]);

    return { reservedHours, loading };
}
